import { isUserMessage } from '@cascade/react/conversations';
import type { TMessage } from '@cascade/react/conversations';
import {
  useExperience,
  useExperienceChat,
  useExperienceChatThread,
} from '@cascade/react/experiences';
import { useI18n } from '@cascade/react/i18n';
import { useMediaQuery } from '@cascade/react-dom/hooks';
import { Badge, Button, Drawer, Text } from '@cascade/ui-library';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classes from './ExperienceChat.module.scss';
import { ChatThread } from '#components/chat-thread/index.ts';
import { WIDE_MEDIA_QUERY } from '#config/index.ts';
import { buildSuggestions } from '#helpers/index.ts';
import { useLayout } from '#layout/index.ts';

export const ExperienceChat = () => {
  const { translate } = useI18n();
  const { isRegular, asideElement } = useLayout();
  const isWide = useMediaQuery(WIDE_MEDIA_QUERY);
  const { currentStep, isStepBusy } = useExperience();
  const { hasPendingAnswer, ask, removeRequest } = useExperienceChat();
  const { messages, followUpQuestions } = useExperienceChatThread();

  const answerCount = messages.filter(
    (message) => !isUserMessage(message) && message.status === 'complete',
  ).length;

  const [isOpen, setIsOpen] = useState(isWide);
  const [seenAnswerCount, setSeenAnswerCount] = useState(answerCount);

  const title = translate('experience.chat.title');
  const openLabel = translate('experience.chat.open');
  const collapseLabel = translate('experience.chat.collapse');
  const unreadCount = answerCount - seenAnswerCount;
  const suggestedQuestions =
    followUpQuestions.length > 0
      ? followUpQuestions
      : (currentStep?.suggested_questions ?? []);

  const suggestions = buildSuggestions(suggestedQuestions);

  const greeting: TMessage = {
    id: 'greeting',
    key: 'greeting',
    sender: 'bot',
    message: translate('experience.chat.greeting'),
    status: 'complete',
  };

  const openChat = () => setIsOpen(true);
  const closeChat = () => setIsOpen(false);

  useEffect(() => {
    if (!hasPendingAnswer) {
      return;
    }

    setIsOpen(true);
  }, [hasPendingAnswer]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSeenAnswerCount(answerCount);
  }, [isOpen, answerCount]);

  useEffect(() => {
    if (isRegular) {
      return;
    }

    setIsOpen(false);
  }, [isRegular]);

  const thread = (
    <ChatThread
      messages={[greeting, ...messages]}
      hasPendingAnswer={hasPendingAnswer}
      suggestions={suggestions}
      isDisabled={isStepBusy}
      isCompact
      onAsk={ask}
      onRemoveRequest={removeRequest}
    />
  );

  const unreadBadge =
    unreadCount > 0 ? (
      <Badge
        label={String(unreadCount)}
        aria-label={translate('experience.chat.unread', {
          count: unreadCount,
        })}
        variant='brand'
        size='sm'
      />
    ) : null;

  if (!isRegular) {
    return (
      <>
        <div className={classes['open-chat']}>
          <Button
            iconType='conversation'
            label={openLabel}
            iconOnly
            onClick={openChat}
          />
          {unreadBadge}
        </div>
        <Drawer
          open={isOpen}
          side='right'
          title={title}
          labels={{ close: collapseLabel }}
          contentClassName={classes.drawer}
          onClose={closeChat}
        >
          {thread}
        </Drawer>
      </>
    );
  }

  if (!asideElement) {
    return null;
  }

  const pane = (
    <section
      className={clsx(classes['experience-chat'], {
        [classes.collapsed]: !isOpen,
      })}
      aria-label={title}
    >
      <div className={classes.header}>
        {isOpen ? (
          <Text type='h4' weight='semibold' content={title} compact />
        ) : null}
        <Button
          variant='secondary'
          iconType={isOpen ? 'aside-collapsed-filled' : 'aside-collapsed'}
          label={isOpen ? collapseLabel : openLabel}
          iconOnly
          ghost
          aria-expanded={isOpen}
          onClick={isOpen ? closeChat : openChat}
        />
      </div>
      <div className={classes.thread}>{thread}</div>
      {isOpen ? null : unreadBadge}
    </section>
  );

  return createPortal(pane, asideElement);
};
